import React, { ReactElement, memo, useRef, useState } from 'react';
import { Row, flexRender, Cell } from '@tanstack/react-table';
import styled from 'styled-components';
import { Tooltip, shouldEnableTooltip } from '../Tooltip/Tooltip';
import { Typography } from '../Typography/Typography';
import { defaultColumnStyling, getAlignStyle, getEllipsisOnOverflow, getStickyStyles } from './utils/getStyling';
import * as DataTableTypes from './utils/DataTableTypes';
import { Td } from './DataTableBasicElements/Td';
import { UtilityColumns } from './hooks/columnHooks';
import isEqual from 'react-fast-compare';
import { sanitizeId } from './utils/getCellWidth';

interface Props<D extends object = {}, V = any> {
  rowOriginal: Row<D>['original'];
  border: DataTableTypes.DataTableBorder | undefined;
  firstVisibleColumn: string | undefined;
  cell: Cell<D, V>;
  columnId: string;
  resizeTableColumns: boolean;
}

const DataTableCellComponent = <D extends object = {}, V = any>({
  firstVisibleColumn,
  rowOriginal,
  resizeTableColumns,
  border,
  cell,
  columnId
}: Props<D, V>): ReactElement | null => {
  const value = cell.getValue<D>() as D[keyof D];
  const column = cell.column;
  const meta = column.columnDef.meta as DataTableTypes.CustomColumnProps<D>;
  const defaultWidth = meta?.defaultWidth;
  const cssWidth = `calc(var(--header-${sanitizeId(columnId)}-size) * 1px)`;
  const isSticky = !!meta.sticky;
  const rowDepth = cell.row.depth;
  const indentSize = rowDepth * 16;
  const cellProps = {
    ...(isSticky ? { 'data-sticky-td': true } : {}),
    ...(meta.sticky === 'right' ? { 'data-sticky-first-right-td': true } : {}),
    ...(meta.sticky === 'left' ? { 'data-sticky-first-left-td': true } : {}),
    style: {
      ...defaultColumnStyling,
      width: resizeTableColumns ? undefined : column.getSize()
    }
  };
  const [showTooltip, setTooltipState] = useState(false);
  const typographyRef = useRef<HTMLDivElement>(null);
  const renderedCell = flexRender(column.columnDef.cell, cell.getContext());
  const shouldRenderEllipsisOnOverflow = getEllipsisOnOverflow(meta?.ellipsisOnOverflow, isSticky);
  const customCellStyles = meta?.cellStyles || {};
  const fixedTableWidth = resizeTableColumns
    ? {
        width: cssWidth,
        flex: `1 1 ${cssWidth}`,
        minWidth: cssWidth
      }
    : defaultWidth
      ? { width: `${defaultWidth}px`, minWidth: `${defaultWidth}px`, flex: `1 1 ${defaultWidth}px` }
      : {};
  let dataTest: string | undefined;
  const shouldAddSpaceForChildExpandedColumn = rowDepth && rowDepth > 0 && firstVisibleColumn === columnId;
  if (meta?.cellDataTest) {
    dataTest = typeof meta.cellDataTest === 'string' ? meta.cellDataTest : meta.cellDataTest(value);
  }
  const styles = {
    ...defaultColumnStyling,
    ...cellProps.style,
    ...getStickyStyles(isSticky),
    ...getAlignStyle(meta?.align),
    ...customCellStyles,
    ...fixedTableWidth
  };
  const columnName = meta.columnName;

  const endCellContent = meta?.CellAfterContent ? (
    <StyledEndContent>
      {meta.CellAfterContent?.({ value, row: { original: rowOriginal } } as unknown as DataTableTypes.CellModel<D>)}
    </StyledEndContent>
  ) : null;

  const startCellContent = meta?.CellBeforeContent ? (
    <StyledStartContent>
      {meta.CellBeforeContent?.({ value, row: { original: rowOriginal } } as unknown as DataTableTypes.CellModel<D>)}
    </StyledStartContent>
  ) : null;

  return (
    <Td
      resizeTableColumns={resizeTableColumns}
      key={cell.id}
      border={border}
      {...cellProps}
      dataTest={dataTest}
      columnName={columnName}
      style={styles}
    >
      {shouldAddSpaceForChildExpandedColumn ? <div style={{ padding: `0 ${indentSize}px` }} /> : null}
      {startCellContent}
      {shouldRenderEllipsisOnOverflow ? (
        <Typography
          ref={typographyRef}
          dataTest="overflow-typography"
          tag="div"
          onClick={() => setTooltipState(false)}
          noWrap
          onMouseEnter={() => setTooltipState(shouldEnableTooltip(typographyRef?.current))}
        >
          <Tooltip
            title={<div>{renderedCell}</div>}
            disabled={!showTooltip}
            dataTest="cell-tooltip"
            placement="top-start"
          >
            {renderedCell}
          </Tooltip>
        </Typography>
      ) : (
        renderedCell
      )}
      {endCellContent}
    </Td>
  );
};

export const DataTableCell = memo(DataTableCellComponent, (prevProps, nextProps) => {
  if ([UtilityColumns.SELECTION, UtilityColumns.EXPANDER].includes(prevProps.columnId as UtilityColumns)) {
    return false;
  }
  if (prevProps.cell.column.columnDef.meta?.forceRender) {
    return false;
  }
  return (
    isEqual(prevProps.rowOriginal, nextProps.rowOriginal) &&
    isEqual(prevProps.firstVisibleColumn, nextProps.firstVisibleColumn)
  );
}) as typeof DataTableCellComponent;

const StyledEndContent = styled.div`
  margin-left: ${({ theme }) => theme.spacingSize.m};
`;

const StyledStartContent = styled.div`
  margin-right: ${({ theme }) => theme.spacingSize.m};
`;
